import { memo, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next";
import { RootState, useAppDispatch, useAppSelector } from "../../store/store";
import { GetOptimizationColumns, handleChangeColumnVarying, handleClearAllOptimizationInputColumns, OptimizeSingle, saveOptimizationValue } from "../../store/optimization-slice/optimization";
import { handleNotificationVariant } from "../notifications/_common";
import { enqueueSnackbar } from "../../store/app-slice/app";
import React from "react";
import SingleOptimizationResultDialog from "./SingleOptimizationResultDialog";
import TrainingModelDetails from "../ml-creator/TrainingModelDetails";
import { Box, Button, Checkbox, Divider, FormControl, FormControlLabel, Grid, IconButton, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { DownloadFile } from "../../services/FileServices";
import { EndPoints } from "../../axios/endPoints";
import { handleToggleTrainingModelDetailsDialog } from "../../store/mlcreator-slice/mlcreator";
import DynamicInput from "../common/DynamicInput";
import CloudDownloadTwoToneIcon from '@mui/icons-material/CloudDownloadTwoTone';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface OptimizationModelProps {
    optimizationDataId: string | null,
    optimizationDataName: string | null
}

const OptimizationModel = ({ optimizationDataId, optimizationDataName } : OptimizationModelProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [optimizationName, setOptimizationName] = useState('');
    const [maximizeOutput, setMaximizeOutput] = useState('maximize');
    const handleChangeMaximizeOutput = (event: React.ChangeEvent<HTMLInputElement>) => {
        setMaximizeOutput((event.target as HTMLInputElement).value);
    };

    const { 
        canBeOptimized,
        optimizationColumnsLoading, 
        optimizationDataInputColumns, 
        optimizationDataOutputColumns, 
        optimizationInProgress, 
        optimizationTrainingDatasetId,
        optimizationIsShared,
        optimizationIsDisabled
    } = useAppSelector((state: RootState) => state.optimization);

    function clearAll() {
        dispatch(handleClearAllOptimizationInputColumns());
        setMaximizeOutput('maximize');
    }

    const handleOptimize = useCallback(() => {
        if(!optimizationName) {
            dispatch(
                enqueueSnackbar({
                    message: t(`OPTIMIZATION_NAME_VALIDATION`),
                    options: { variant: "error", persist: true },
                })
            );
            return;
        }

        if (!optimizationDataInputColumns.some((el: any) => el.varying)) {
            dispatch(enqueueSnackbar({ message: t('OPTIMIZATION_VARYING_VALIDATION'), options: { variant: 'error', persist: true } }));
            return;
        }
        if(optimizationDataInputColumns.find((el: any) => !el.value && !el.varying)) {
            dispatch(enqueueSnackbar({ message: t(`OPTIMIZATION_VALIDATION`), options: { variant: 'error', persist: true } }))
            return;
        }
        
        if(optimizationDataId) {
            dispatch(OptimizeSingle({
                name: optimizationName,
                mlTrainedModelId: parseInt(optimizationDataId),
                inputColumns: optimizationDataInputColumns.filter((el: any) => !el.varying),
                varyingColumns: optimizationDataInputColumns.filter((el: any) => { if(el.varying) return el })?.map((el: any) => el.name),
                maximiseOutput: maximizeOutput === 'maximize' ? true : false
            })).then((res : any) => {
                if(res && res.payload) {
                    if(res.payload.success) {
                        setOptimizationName('');
                    } 
                    dispatch(enqueueSnackbar({ 
                        message: t(`${res.payload.message}`), 
                        options: { 
                            variant: handleNotificationVariant(res.payload.success),
                            persist: !res.payload.success
                        } 
                    }));
                }
            });
        }
    }, [dispatch, optimizationDataId, optimizationName, optimizationDataInputColumns, t]);

    useEffect(() => {
        if(optimizationDataId) {
            dispatch(GetOptimizationColumns(parseInt(optimizationDataId)));
            setOptimizationName(''); //Reset Optimization Name on model change
            setMaximizeOutput('maximize'); //Reset Maximize Output checkbox
        }
    }, [optimizationDataId])

    return(
        <React.Fragment>
            { optimizationColumnsLoading ? (
                <Grid mt={1.5} container display="flex" justifyContent="center">
                    <Grid item>
                        <Typography variant="body1">{t('OPTIMIZATION_LOADING_COLUMNS')}</Typography>
                    </Grid>
                </Grid> 
            ) : (
                <React.Fragment>
                    {
                        !optimizationDataInputColumns?.length ? <Grid mt={1.5} container display="flex" justifyContent="center">
                            <Grid item><Typography variant="body1">{t('OPTIMIZATION_NO_COLUMNS_AVAILABLE')}</Typography></Grid>
                        </Grid> : 
                        !canBeOptimized ? <Grid mt={1.5} container display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                            <Grid item><Typography fontSize="20px" fontWeight="bold" color="red">{t('CAN_BE_OPTIMIZED_FALSE_TITLE')}</Typography></Grid>
                            <Grid item><Typography fontSize="18px" color="red">{t('CAN_BE_OPTIMIZED_FALSE_BODY')}</Typography></Grid>
                        </Grid> :
                        <Grid display="flex" justifyContent="center" flexDirection="column">
                            <Grid container mb={1.5} display="flex" justifyContent="space-between" alignItems="center">
                                <Grid item>
                                    <Grid container spacing={.5} display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                                        {!optimizationIsShared && (
                                        <Grid item>
                                            <IconButton onClick={(e) => {
                                                e.stopPropagation();
                                                DownloadFile(EndPoints.ML_CREATOR_DOWNLOAD_TRAINING_DATASET + `?id=${optimizationTrainingDatasetId}`)
                                            }}>
                                                <CloudDownloadTwoToneIcon className="download-icon" />
                                            </IconButton>
                                        </Grid>
                                        )}
                                        <Grid item>
                                            <IconButton  onClick={() => {
                                                if(optimizationDataId) {
                                                    dispatch(handleToggleTrainingModelDetailsDialog({ 
                                                        isOpen: true,
                                                        body: { 
                                                            id: parseInt(optimizationDataId),
                                                            name: optimizationDataName
                                                        }
                                                    }))
                                                }
                                            }}>
                                                <InfoOutlinedIcon className="info-icon" />
                                            </IconButton>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="h6">
                                                {t('OPTIMIZATION_MODEL_NAME', { name: optimizationDataName })}
                                                {optimizationIsDisabled && (
                                                    <span style={{ fontWeight: 'bold', marginLeft: '8px' }} className="info-icon">
                                                        [{t('OPTIMIZATION_DISABLED_MODEL')}]
                                                    </span>
                                                )}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <fieldset disabled={optimizationIsDisabled} style={{
                                border: 0,
                                padding: 0,
                                margin: 0,
                                opacity: optimizationIsDisabled ? 0.5 : 1,
                                pointerEvents: optimizationIsDisabled ? "none" : "auto"
                            }}>
                            <Grid container spacing={2} display="flex" flexDirection="column" pb={1}>
                                <Grid item>
                                    <Grid 
                                        container 
                                        alignItems="center" 
                                        justifyContent="space-between"
                                    >
                                        <Grid item>
                                            <TextField 
                                                size="small" 
                                                sx={{ minWidth: 300 }}
                                                label={t('OPTIMIZATION_NAME_TEXTFIELD_LABEL')}
                                                value={optimizationName}
                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                    setOptimizationName(event.target.value);
                                                }}
                                            />
                                        </Grid>

                                        <Grid item>
                                            <Button 
                                                size="small" 
                                                onClick={() => clearAll()} 
                                                className="button-text-color"
                                                style={{ border: '1px solid var(--button-background-color)' }}
                                            >
                                                {t('OPTIMIZATION_CLEAR_ALL_BUTTON')}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Divider />
                                </Grid>
                            </Grid>
                            <Grid container spacing={2} mb={2}>
                                {optimizationDataInputColumns.map((column:any, index: number) => (
                                    <Grid 
                                        item 
                                        key={column.name} 
                                        xs={12} 
                                        sm={6} 
                                        display="flex" 
                                        flexDirection={{ xs: 'column', sm: 'row' }}  
                                        justifyContent="space-between"
                                        gap={1}
                                    >
                                        <Box flexGrow={1}>
                                            <DynamicInput
                                                columns={optimizationDataInputColumns}
                                                name={column.name}
                                                type={column.type}
                                                index={index}
                                                onSave={saveOptimizationValue}
                                                disabled={column.varying}
                                                sx={{
                                                    ...(column.varying
                                                        ? {
                                                            '& .MuiInputBase-input': {
                                                                backgroundColor: 'var(--button-background-color)',
                                                            },
                                                            '& .MuiInputLabel-root': {
                                                                color: 'var(--button-text-color)',
                                                            },
                                                        }
                                                        : {}),
                                                }}
                                            />
                                        </Box>
                                        <Box>
                                            {column.canBeOptimized ? (
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            disabled={!column.canBeOptimized}
                                                            checked={column.varying}
                                                            onChange={() => {
                                                                if (column.canBeOptimized) dispatch(handleChangeColumnVarying(column.name));
                                                            }}
                                                            size="small"
                                                            sx={{
                                                                '& .MuiSvgIcon-root': { fontSize: 18 },
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                                            {t('VARYING_COLUMN_CHECKBOX_LABEL')}
                                                        </Typography>
                                                    }
                                                    sx={{
                                                        margin: 0,
                                                    }}
                                                />
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        fontSize: '0.85rem',
                                                        paddingLeft: '8px',
                                                    }}
                                                >
                                                    N/A
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                            <Grid mb={2}>
                                <FormControl>
                                    <RadioGroup
                                        aria-labelledby="controlled-radio-buttons-group"
                                        name="controlled-radio-buttons-group"
                                        value={maximizeOutput}
                                        onChange={handleChangeMaximizeOutput}
                                    >
                                        <Grid display="flex" flexDirection="row" alignItems="center">
                                            <FormControlLabel 
                                                value="maximize" 
                                                control={
                                                    <Radio />
                                                } 
                                                label={t('MAXIMIZE_OUTPUT_CHECKBOX_LABEL')} 
                                            />
                                            <label style={{ fontWeight: 'bold' }}>{ optimizationDataOutputColumns?.map((el: string) => el).join(', ') }</label>
                                        </Grid>
                                        <Grid display="flex" flexDirection="row" alignItems="center">
                                            <FormControlLabel 
                                                value="minimize" 
                                                control={
                                                    <Radio />
                                                } 
                                                label={t('MINIMIZE_OUTPUT_CHECKBOX_LABEL')} 
                                            />
                                            <label style={{ fontWeight: 'bold' }}>{ optimizationDataOutputColumns?.map((el: string) => el).join(', ') }</label>
                                        </Grid>
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Button 
                                disabled={optimizationInProgress} 
                                onClick={() => handleOptimize()} 
                                variant="contained"
                                className='button-color'
                                size="small"
                            >{t('OPTIMIZATION_BUTTON')}</Button>
                            </fieldset>
                        </Grid>
                    }
                </React.Fragment>
            )}
            <SingleOptimizationResultDialog />
            <TrainingModelDetails />
        </React.Fragment>
    );
}

export default memo(OptimizationModel);